import { Fragment, ReactNode, useRef } from 'react';

import { usePathname } from 'next/navigation';
import Dropdown from 'rc-dropdown';
import 'rc-dropdown/assets/index.css';
import 'rc-menu/assets/index.css';
import { Control, Controller } from 'react-hook-form';

import ArrowDown from '@/assets/icons/icon-dropdown.svg';
import HelpMenuDropdown from '@/components/shared/HelpMenuDropDown';
import { useLoanTokens } from '@/hooks/query/loans/useLoanTokens';
import { cn, sortCurrencySymbols } from '@/utils/helper';

export type TContentArr = {
  key?: string;
  value?: string | number;
  unit?: string;
  formKey?: string;
  disable?: boolean;
  CustomField?: () => React.ReactNode;
  max?: number;
  containerClassName?: string;
  keyClassName?: string;
  renderAdditionalField?: React.ReactNode;
  customOnChange?: (newValue: string | null, newValue2?: string) => void;
}[];

export interface IDesiredTermsProps {
  title?: {
    simple: string;
    highlight?: string;
  };
  Actions?: () => JSX.Element;
  control?: Control<any>;
  content: TContentArr;
  subtitle?: string;
  Profit?: () => JSX.Element;
  hideDropDown?: boolean;
  simplified?: boolean;
  className?: string;
}

interface IDesiredTermsItemContainerProps {
  children: ReactNode;
  containerClassName?: string;
  simplified?: boolean;
}

interface IDesiredTermsLabelProps {
  label: string;
  labelClassName?: string;
}

export const DesiredTermsItemContainer: React.FC<IDesiredTermsItemContainerProps> = ({ children, containerClassName, simplified }) => {
  return <div className={cn('bg-white dark:bg-jet-black dark:border-coal-border border  flex justify-between h-10 item relative', simplified ? 'w-full px-3 gap-3' : 'min-w-[260px] px-6 gap-5 rounded', containerClassName)}>{children}</div>;
};

export const DesiredTermsLabel: React.FC<IDesiredTermsLabelProps> = ({ label, labelClassName }: IDesiredTermsLabelProps) => (
  <div className='flex items-center !bg-transparent'>
    <p className={cn('text-base/4 font-medium border-r-white dark:border-r-jet-black py-0.5 lg:py-[17px]', labelClassName)}>{label}</p>
  </div>
);

const DesiredTerms: React.FC<IDesiredTermsProps> = ({ title, Actions, content, subtitle, Profit, hideDropDown, control, className, simplified }) => {
  const { data: menu } = useLoanTokens();
  const pathname = usePathname();
  const dropdownParentRef = useRef<HTMLDivElement | null>(null);

  const sortedMenu = sortCurrencySymbols(menu || []);
  return (
    <div className={cn('flex flex-col flex-1 wrapper m-auto max-w-[587px]', pathname.endsWith('/active') && 'max-lg:dark:!bg-char-coal', pathname.endsWith('/offers') && '!bg-white dark:!bg-jet-black-100', className)}>
      {title && (
        <h5 className='font-semibold text-2xl/7 dark:!text-white !text-coal-250 text-center'>
          {title.simple} {title.highlight && <span className='text-primary text-lg/5'>{title.highlight}</span>}
        </h5>
      )}
      {subtitle && <span className='text-center block font-normal text-[13px]/7'>{subtitle}</span>}
      <div className={cn('grid grid-cols-2 content', simplified ? 'mt-[11px] gap-0' : 'gap-x-5 gap-y-[13px] pt-8')}>
        {content.map(({ key, formKey, value, unit, CustomField, disable, max, containerClassName, keyClassName, customOnChange }, idx) => {
          return (
            <Fragment key={key || idx}>
              {CustomField
                ? CustomField()
                : key &&
                  formKey && (
                    <DesiredTermsItemContainer containerClassName={containerClassName} simplified>
                      <DesiredTermsLabel label={key} labelClassName={keyClassName} />
                      <div className='flex items-center flex-1 justify-end'>
                        {control ? (
                          <Controller
                            name={formKey}
                            control={control}
                            defaultValue={value}
                            render={({ field }) => {
                              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                const newValue = e.target.value;
                                if (!Number.isNaN(Number(newValue)) && max && newValue.length <= max) {
                                  field.onChange(newValue);
                                  customOnChange?.(newValue);
                                }
                              };

                              return (
                                <div className='relative !bg-transparent w-full'>
                                  <input
                                    {...field}
                                    onChange={handleChange}
                                    onFocus={(e) => {
                                      if (e.target.value === '0') {
                                        field.onChange('');
                                      }
                                    }}
                                    onBlur={(e) => {
                                      if (!e.target.value) {
                                        field.onChange('0');
                                      }
                                    }}
                                    className='!text-coal-300 dark:!text-coal-50 text-sm !bg-transparent !caret-char-coal dark:!caret-white w-full text-right leading-[26px] font-space-mono'
                                    disabled={disable}
                                    type='number'
                                    {...(value && { value })}
                                  />
                                </div>
                              );
                            }}
                          />
                        ) : (
                          <p className='text-coal-300 dark:text-coal-50 text-sm font-normal flex items-center !bg-transparent'>{value}</p>
                        )}
                        {!hideDropDown && idx === 0 && sortedMenu && (
                          <div ref={dropdownParentRef} className='flex items-center !bg-transparent'>
                            <Controller
                              name='currency'
                              defaultValue={sortedMenu.find(({ symbol }) => symbol === 'USDC')?.address}
                              control={control}
                              render={({ field }) => {
                                return (
                                  <Dropdown
                                    getPopupContainer={() => dropdownParentRef.current!}
                                    animation='slide-up'
                                    overlay={
                                      <HelpMenuDropdown
                                        customStyle='top-1 right-1'
                                        menu={sortedMenu.map(({ address, symbol }) => ({ value: address, label: symbol }))}
                                        categoryHandler={(menuItem) => {
                                          field.onChange(menuItem);
                                          customOnChange?.(null, menuItem);
                                        }}
                                      />
                                    }
                                    trigger={['click']}
                                  >
                                    <div className='ml-1 gap-1 py-1 px-2 bg-white-100 dark:!bg-jet-black-100 flex justify-between rounded-[2px] items-center'>
                                      <span className='text-[10px]/3 placeholder:text-[10px]/3 text-coal-250'>{sortedMenu.find(({ address }) => address === field.value)?.symbol}</span>
                                      <span>
                                        <ArrowDown className='dark:fill-white [&_path]:dark:fill-white w-[10px] h-[6px]' />
                                      </span>
                                    </div>
                                  </Dropdown>
                                );
                              }}
                            />
                          </div>
                        )}
                        {unit && <p className='text-coal-300 dark:text-coal-50 text-sm font-normal flex items-center font-space-mono ml-1'>{unit}</p>}
                      </div>
                    </DesiredTermsItemContainer>
                  )}
            </Fragment>
          );
        })}
      </div>
      {Profit && Profit()}
      {Actions && Actions()}
    </div>
  );
};

export default DesiredTerms;
